import { Card, CardContent, Box, Typography, CircularProgress } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

/**
 * MetricCard Component
 * Displays a metric with icon, value, title, subtitle, and optional trend
 *
 * @param {string} title - Main metric title
 * @param {number|string} value - Metric value
 * @param {ReactNode} icon - Icon component
 * @param {string} subtitle - Optional subtitle/description
 * @param {Object} trend - Optional trend data {value: number, direction: 'up'|'down'}
 * @param {boolean} loading - Loading state
 * @param {string} color - Color theme ('primary', 'success', 'error', 'warning', 'info')
 */
const MetricCard = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  loading = false,
  color = 'primary',
}) => {
  const getTrendColor = () => {
    if (!trend) return 'text.secondary';
    return trend.direction === 'up' ? 'success.main' : 'error.main';
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    return trend.direction === 'up' ? (
      <TrendingUp fontSize="small" />
    ) : (
      <TrendingDown fontSize="small" />
    );
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: `${color}.main`,
        transition: 'all 0.3s',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          {/* Title */}
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            {title}
          </Typography>

          {/* Icon */}
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: `${color}.lighter`,
              color: `${color}.main`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>

        {/* Value */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
              {value}
            </Typography>

            {/* Subtitle or Trend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: getTrendColor(),
                  }}
                >
                  {getTrendIcon()}
                  <Typography variant="body2" fontWeight="medium">
                    {trend.value}%
                  </Typography>
                </Box>
              )}
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: trend ? 1 : 0 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
